"use client"

import type { Dependency, FieldConfig } from "@/components/ui/auto-form/types"
import type { UnitFormValues } from "@/mappers/competition/units-form.mapper"
import type { TSettings } from "@/models/discipline.model"
import type { Unit, UnitBody } from "@/models/units.model"
import { useQuery } from "@tanstack/react-query"
import { useTranslations } from "next-intl"
import { useMemo, useRef, useState } from "react"
import { toast } from "sonner"
import { z } from "zod"
import AutoFormComponent from "@/components/autoform-component"
import { DURATION_CONFIG } from "@/components/ui/auto-form/fields/format-configs"
import { DependencyType } from "@/components/ui/auto-form/types"
import { Button } from "@/components/ui/button"
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "@/components/ui/dialog"
import { UnitsFormMapper } from "@/mappers/competition/units-form.mapper"
import { MedalsQueryOptions } from "@/queries/disciplines.factory"
import { LocationQueryOptions } from "@/queries/locations.factory"
import {
  createDateFromString,
  createUTCDate,
  extractUTCDateAsLocal,
  extractUTCTimeAsString,
} from "@/utils/date-utils"

// Interface para controlar qué campos se muestran
export interface FieldVisibilityConfig {
  unitsNumber?: boolean
  name?: boolean
  longDescription?: boolean
  shortDescription?: boolean
  medals?: boolean
  medalCodes?: boolean
  medalCountConfig?: boolean
  order?: boolean
  unitDate?: boolean
  timeStart?: boolean
  timeEnd?: boolean
  timegap?: boolean
  venue?: boolean
  location?: boolean
}

type ConfigurableUnitDialogProps = {
  isOpen: boolean
  onClose: () => void
  onSave: (unitData: UnitBody) => Promise<void>
  unitData?: Unit
  phaseId: string
  disciplineId?: string
  isSaving?: boolean
  phaseSettings?: TSettings
  order?: number
  language?: string
  // Nueva prop para controlar visibilidad de campos
  fieldVisibility?: FieldVisibilityConfig
  // Props opcionales para personalizar el modal
  title?: string
  submitButtonText?: string
  cancelButtonText?: string
}

export function ConfigurableUnitDialog({ 
  isOpen, 
  onClose, 
  onSave, 
  unitData, 
  phaseId, 
  isSaving, 
  disciplineId, 
  phaseSettings, 
  order, 
  language,
  fieldVisibility = {},
  title,
  submitButtonText,
  cancelButtonText
}: ConfigurableUnitDialogProps) {
  const t = useTranslations()
  const [formValues, setFormValues] = useState<Partial<z.infer<typeof unitSchema>>>(getInitialValue())

  // Estado para el venue seleccionado - inicializar con el valor de unitData si existe
  const [selectedVenue, setSelectedVenue] = useState<string | undefined>(() => {
    if (unitData) {
      const formValues = UnitsFormMapper.toFormValues(unitData)
      return formValues.venue
    }
    return undefined
  })

  // Callback para manejar cambios en el formulario
  const handleFormChange = (values: Partial<z.infer<typeof unitSchema>>) => {
    // Actualizar el estado del formulario
    setFormValues(prev => ({ ...prev, ...values }))
    
    if (values.venue !== selectedVenue) {
      setSelectedVenue(values.venue)
    }
  }

  const {
    data: availableMedals,
  } = useQuery(MedalsQueryOptions.all())

  const { data: availableLocations } = useQuery(LocationQueryOptions.all)

  // Obtener rango de fechas disponibles desde eventSettings
  const availableDateRange = phaseSettings?.dateInfo
    ? {
        minDate: phaseSettings.dateInfo.startDate,
        maxDate: phaseSettings.dateInfo.endDate,
      }
    : { minDate: undefined, maxDate: undefined }
  // Calcular la primera fecha disponible para usar como default
  const defaultStartDate = availableDateRange.minDate || undefined
  const defaultEndDate = availableDateRange.maxDate || undefined

  const unitSchema = z.object({
    unitsNumber: z.coerce.number().min(1, { message: t("settings.units.number_required") }).optional(),
    name: z.string().min(3, { message: t("settings.units.name_required") }).optional(),
    longDescription: z.string().min(3, { message: t("settings.units.long_description_required") }).optional(),
    shortDescription: z.string().optional(),
    medals: z.boolean().optional(),
    medalCodes: z.array(z.string()).optional(),
    medalCountConfig: z.record(z.string(), z.number()).optional(),
    order: z.coerce.number().min(1, { message: t("settings.units.order_required") }).optional(),
    unitDate: z.date().refine(date => !Number.isNaN(date.getTime()), {
      message: t("settings.units.start_date_required"),
    }),
    timeStart: z.string().optional(),
    timeEnd: z.string().optional(),
    timegap: z.string().optional(),
    venue: z.string().optional(),
    location: z.string().optional(),
  })

  const formatDateForInput = (date: Date | string | undefined): string | undefined => {
    if (!date)
      return undefined

    // Si es un string, usar createDateFromString para evitar conversión de zona horaria
    if (typeof date === "string") {
      const localDate = createDateFromString(date)
      if (!localDate)
        return undefined

      // Formatear directamente sin usar toISOString()
      const year = localDate.getFullYear()
      const month = String(localDate.getMonth() + 1).padStart(2, "0")
      const day = String(localDate.getDate()).padStart(2, "0")
      return `${year}-${month}-${day}`
    }

    // Si es un Date, crear uno nuevo sin conversión de zona horaria
    const year = date.getFullYear()
    const month = date.getMonth()
    const day = date.getDate()
    const localDate = new Date(year, month, day, 0, 0, 0, 0)

    // Formatear directamente sin usar toISOString()
    const monthStr = String(localDate.getMonth() + 1).padStart(2, "0")
    const dayStr = String(localDate.getDate()).padStart(2, "0")
    const yearStr = localDate.getFullYear()
    const stringDate = `${yearStr}-${monthStr}-${dayStr}`
    console.log("stringDate", stringDate)
    return stringDate
  }

  const venuesOptions = useMemo(() => {
    return phaseSettings?.venues?.map(venue => ({
      value: venue.code,
      label: venue.description?.long,
    })) || []
  }, [phaseSettings?.venues])

  const locationOptions = useMemo(() => {
    if (!availableLocations)
      return []

    // Si no hay venue seleccionado, mostrar todas las locations filtradas por disciplina
    const filteredByDiscipline = availableLocations.filter((location) => {
      // location.discipline es un array de strings
      if (!disciplineId)
        return true
      return Array.isArray(location.discipline) && location.discipline.includes(disciplineId)
    })

    if (!selectedVenue) {
      return filteredByDiscipline.map(location => ({
        value: String(location.id),
        label: location.description.long,
      }))
    }

    // Filtrar locations por venue seleccionado y disciplina
    const filteredLocations = filteredByDiscipline.filter((location) => {
      if (typeof location.venue === "string") {
        return location.venue === selectedVenue
      }
      return location.venue.code === selectedVenue
    })

    return filteredLocations.map(location => ({
      value: String(location.id),
      label: location.description.long,
    }))
  }, [availableLocations, selectedVenue, disciplineId])

  // Configure field appearance and behavior - Solo mostrar fecha, hora y localización
  const fieldConfig: FieldConfig<z.infer<typeof unitSchema>> = {
    unitsNumber: {
      fieldType: "number",
      inputProps: {
        min: 1,
        step: 1,
        disabled: !!unitData,
        hidden: true, // Oculto por defecto
      },
    },
    name: {
      label: t("settings.units.name"),
      description: t("settings.units.name_description"),
      inputProps: {
        hidden: true, // Oculto por defecto
      },
    },
    longDescription: {
      label: t("settings.units.long_description"),
      description: t("settings.units.long_description_help"),
      inputProps: {
        hidden: true, // Oculto por defecto
      },
    },
    shortDescription: {
      label: t("settings.units.short_description"),
      description: t("settings.units.short_description_help"),
      inputProps: {
        hidden: true, // Oculto por defecto
      },
    },
    medals: {
      label: t("settings.units.medals"),
      description: t("settings.units.medals_description"),
      fieldType: "checkbox",
      inputProps: {
        disabled: !phaseSettings?.hasMedals,
        hidden: true, // Oculto por defecto
      },
    },
    medalCodes: {
      label: t("settings.units.medal_codes"),
      description: t("settings.units.medal_codes_description"),
      fieldType: "extendedOptions",
      inputProps: {
        options: availableMedals?.map(medal => ({
          value: medal.code,
          label: medal.description.long,
        })) || [],
        multiple: true,
        hidden: true, // Oculto por defecto
      },
    },
    order: {
      label: t("settings.units.order"),
      description: t("settings.units.order_description"),
      fieldType: "number",
      inputProps: {
        disabled: !unitData,
        hidden: true, // Oculto por defecto
      },
    },
    unitDate: {
      label: t("settings.units.unit_date"),
      description: t("settings.units.unit_date_description"),
      fieldType: "date",
      inputProps: {
        min: formatDateForInput(availableDateRange.minDate),
        max: formatDateForInput(availableDateRange.maxDate),
        disableAllDatesWithoutRange: true,
        hidden: false, // Visible - FECHA
      },
    },
    timeStart: {
      label: t("settings.units.time_start"),
      description: t("settings.units.time_start_description"),
      fieldType: "time",
      inputProps: {
        hidden: false, // Visible - HORA INICIO
      },
    },
    timeEnd: {
      label: t("settings.units.end_time"),
      description: t("settings.units.time_end_description"),
      fieldType: "time",
      inputProps: {
        hidden: false, // Visible - HORA FIN
      },
    },
    timegap: {
      label: t("settings.units.timegap"),
      description: t("settings.units.timegap_description"),
      fieldType: "formattedInput",
      inputProps: {
        formatconfig: DURATION_CONFIG,
        hidden: true, // Oculto por defecto
      },
    },
    venue: {
      label: t("settings.units.venue"),
      description: t("settings.units.venue_description"),
      fieldType: "extendedOptions",
      inputProps: {
        options: venuesOptions,
        hidden: true, // Oculto por defecto
      },
    },
    medalCountConfig: {
      label: t("settings.units.medal_count_config"),
      description: t("settings.units.medal_count_config_description"),
      fieldType: "medalCountConfig",
      inputProps: {
        maxTotalMedals: 4, // Máximo total de medallas entre todas las categorías
        hidden: true, // Oculto por defecto
      },
    },
    location: {
      label: t("settings.units.area"),
      description: t("settings.units.area_description"),
      fieldType: "extendedOptions",
      inputProps: {
        options: locationOptions,
        multiple: false,
        hidden: false, // Visible - LOCALIZACIÓN
      },
    },
  }

  const dependencies: Dependency<z.infer<typeof unitSchema>>[] = [
    {
      sourceField: "medals",
      type: DependencyType.DISABLES,
      targetField: "medalCodes",
      when: (medalsValue) => {
        return !medalsValue
      },
    },
    {
      sourceField: "medals",
      type: DependencyType.HIDES,
      targetField: "medalCountConfig",
      when: (medalsValue) => {
        return !medalsValue
      },
    },
    {
      sourceField: "unitsNumber",
      type: DependencyType.HIDES,
      targetField: "timegap",
      when: (sourceFieldValue: unknown) => {
        const value = Number(sourceFieldValue)
        return Number.isNaN(value) || value <= 1
      },
    },
  ]

  function getInitialValue() {
    if (unitData) {
      const formValues = UnitsFormMapper.toFormValues(unitData)
      console.log("Initial values for unit form:", formValues)

      return {
        unitsNumber: formValues.unitsNumber,
        name: formValues.name,
        longDescription: formValues.longDescription,
        shortDescription: formValues.shortDescription,
        medals: phaseSettings?.hasMedals || formValues.hasMedals || false,
        medalCodes: formValues.medalCodes || [],
        medalCountConfig: formValues.medalQuantities || {},
        order: formValues.order,
        startDate: formValues.startDate,
        unitDate: extractUTCDateAsLocal(formValues.startDate.toISOString()), // Convertir Date a string
        endDate: formValues.endDate,
        location: formValues.location,
        venue: formValues.venue,
        timeStart: extractUTCTimeAsString(formValues.startDate), // Esta función ya maneja Date
        timeEnd: extractUTCTimeAsString(formValues.endDate), // Esta función ya maneja Date
        timegap: formValues.timegap || "00:10:00",
      }
    }

    return {
      unitsNumber: 1,
      name: "",
      longDescription: "",
      shortDescription: "",
      medals: phaseSettings?.hasMedals || false,
      medalCodes: [],
      medalCountConfig: {},
      order: order ?? 1,
      unitDate: defaultStartDate ? new Date(defaultStartDate) : undefined,
      startDate: defaultStartDate ? new Date(defaultStartDate) : new Date(),
      endDate: defaultEndDate ? new Date(defaultEndDate) : new Date(),
      venue: venuesOptions.length === 1 ? venuesOptions[0]!.value : undefined,
      location: "",
      timeStart: "",
      timeEnd: "",
      timegap: "00:10:00",
    }
  }

  const handleSubmit = (values: z.infer<typeof unitSchema>) => {
    console.log("=== FORMULARIO ENVIADO ===")
    console.log("Valores del formulario:", values)
    
    // Crear startDate y endDate como fechas UTC
    const startDate = createUTCDate(values.unitDate, values.timeStart)
    const endDate = createUTCDate(values.unitDate, values.timeEnd)
    
    console.log("StartDate creado:", startDate)
    console.log("EndDate creado:", endDate)

    // Convertir los valores del schema a UnitFormValues
    // IMPORTANTE: Siempre se envía toda la información, independientemente de qué campos están visibles
    const formValues: UnitFormValues = {
      unitsNumber: values.unitsNumber || 1,
      name: values.name || "",
      longDescription: values.longDescription || "",
      shortDescription: values.shortDescription || "",
      hasMedals: phaseSettings?.hasMedals || values.medals || false, // Si phaseSettings?.hasMedals es true, fuerza hasMedals a true
      medalCodes: values.medalCodes || [],
      medalQuantities: values.medalCountConfig || {},
      timegap: values.timegap || "",
      order: values.order || 1,
      startDate,
      endDate,
      location: String(values.location || ""),
      venue: values.venue,
    }
    
    console.log("FormValues mapeados:", formValues)

    try {
      // Usar el mapper para convertir valores del formulario a UnitBody
      const data = UnitsFormMapper.toApiBody(formValues, phaseId, unitData, disciplineId, language)
      console.log("=== DATOS FINALES A GUARDAR ===")
      console.log("Unit data to save:", data)
      console.log("PhaseId:", phaseId)
      console.log("UnitData:", unitData)
      console.log("DisciplineId:", disciplineId)
      console.log("Language:", language)
      console.log("================================")
      void onSave(data)
    }
    catch (error) {
      console.error("Error saving unit:", error)
      toast.error(t("settings.units.save_error"))
    }
  }

  // Determinar el título del modal
  const modalTitle = title || (unitData ? t("settings.units.edit") : t("settings.units.add"))

  return (
    <Dialog onOpenChange={onClose} open={isOpen}>
      <DialogContent className="max-h-[90vh] overflow-y-auto sm:max-w-[700px]">
        <DialogHeader>
          <DialogTitle>
            {modalTitle}
          </DialogTitle>
        </DialogHeader>

        <div className="space-y-6 py-4">
          <AutoFormComponent
            columns={2}
            dependencies={dependencies}
            fieldConfig={fieldConfig}
            formSchema={unitSchema}
            initialValues={getInitialValue()}
            layout="grid"
            onChange={handleFormChange}
            onSubmit={handleSubmit}
            submitButton={null}
          />
          
          {/* Botones separados del formulario */}
          <div className="mt-6 flex justify-end gap-2">
            <Button
              disabled={isSaving}
              onClick={onClose}
              type="button"
              variant="outline"
            >
              {cancelButtonText || t("generic.cancel")}
            </Button>
            <Button 
              disabled={isSaving} 
              onClick={() => {
                console.log("Botón de guardar clickeado")
                console.log("Valores actuales del formulario:", formValues)
                handleSubmit(formValues as z.infer<typeof unitSchema>)
              }}
              type="button"
            >
              {isSaving ? t("generic.saving") : (submitButtonText || t("generic.save"))}
            </Button>
          </div>
        </div>
      </DialogContent>
    </Dialog>
  )
}
